import {
  ArrowLeftIcon,
  QueueListIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import { useEffect, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ChatMessagesLayout from "../../components/layout/chat-messages-layout";
import ChatNavigation from "../../components/chat/chat-navigation";
import { SubmitButton } from "../../components/shared/buttons/submit-button";
import Typography from "../../components/shared/typography";
import { useMessageContext } from "../../hooks/MessageContext";
import { useUserDetails } from "../../hooks/useUserDetails";

const AdminUserChatPage = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { userDetails, isLoading: isUserLoading } = useUserDetails();
  const isAdmin = userDetails?.type === "admin";
  const { setChatType } = useMessageContext();

  const stateUser =
    location.state && typeof location.state === "object"
      ? (location.state as {
          user?: { first_name?: string | null; last_name?: string | null };
        }).user
      : undefined;

  useEffect(() => {
    if (isAdmin && userId) {
      setChatType("all", userId);
      return () => {
        setChatType("ai_chat");
      };
    }
  }, [isAdmin, setChatType, userId]);

  const displayName = useMemo(() => {
    if (stateUser && (stateUser.first_name || stateUser.last_name)) {
      return `${stateUser.first_name ?? ""} ${stateUser.last_name ?? ""}`.trim();
    }
    return userId ?? "Selected conversation";
  }, [stateUser, userId]);

  if (!isAdmin && !isUserLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto text-center space-y-4">
          <Typography variant="h3" className="text-terracotta">
            Admin access required
          </Typography>
          <Typography variant="body1" className="text-brown-600">
            You need an administrator account to view these chats.
          </Typography>
        </div>
      </div>
    );
  }

  if (!userId) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto text-center space-y-4">
          <Typography variant="h3" className="text-terracotta">
            Conversation not found
          </Typography>
          <Typography variant="body1" className="text-brown-600">
            We couldn't determine which chat to load.
          </Typography>
        </div>
      </div>
    );
  }

  const headerHeight = "4rem";
  const chatInputHeight = "5rem";

  return (
    <main
      className="max-w-8xl mx-auto px-4 py-8 sm:px-6 lg:px-8"
      style={{ height: "var(--app-height)" }}
    >
      <ChatNavigation />
      <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-terracotta-100 flex items-center justify-center">
            <UserIcon className="w-6 h-6 text-terracotta-600" />
          </div>
          <div>
            <Typography variant="h3" className="text-terracotta">
              {displayName}
            </Typography>
            <Typography variant="body2" className="text-brown-600">
              Viewing Harper conversation as admin
            </Typography>
          </div>
        </div>
        <div className="flex gap-2">
          <SubmitButton
            size="sm"
            color="beige"
            onClick={() => navigate("/admin/all-chats")}
            icon={<ArrowLeftIcon className="w-4 h-4" />}
          >
            Back to list
          </SubmitButton>
        </div>
      </div>
      <div
        className="relative h-full"
        style={{
          height: `calc(var(--app-height) - ${headerHeight} - ${chatInputHeight})`,
          marginBottom: chatInputHeight,
        }}
      >
        <ChatMessagesLayout />
      </div>
      <div className="mt-4 flex items-center gap-2 text-brown-500 text-sm">
        <QueueListIcon className="w-4 h-4" />
        <span>Read-only view</span>
      </div>
    </main>
  );
};

export default AdminUserChatPage;
